import React, { useRef, useState, useEffect, useContext } from "react";
import {
  Input,
  Table,
  Form,
  Select,
  InputNumber,
  Button,
  DatePicker,
} from "antd";
import { Prompt } from "react-router-dom";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { randomNumberGenerator } from "utils/general";
import Locale from "react-intl-universal";

const EditableContext = React.createContext(null);

const EditableType = {
  number: "number",
  text: "text",
  selector: "selector",
  date: "date",
};

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  setTimeout(() => {
    form.validateFields();
  }, 250);
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  editableType,
  selection,
  mandatory,
  ...restProps
}) => {
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  const [editing, setEditing] = useState(false);
  const [selectionBuffer, setSelection] = useState([]);

  useEffect(() => {
    if (editing && editableType !== "type") {
      try {
        inputRef.current.focus();
      } catch (error) {
        console.log(error);
      }
    }
  }, [editing]);

  useEffect(() => {
    // Config selector
    if (editableType !== EditableType.selector) {
      return;
    }
    let buffer = [...selection];
    setSelection(buffer);
  }, []);

  const toggleEdit = () => {
    setEditing(!editing);
    if (record) {
      form.setFieldsValue({
        [dataIndex]: record[dataIndex],
      });
    }
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values }, record.key);
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  let childNode = children;

  switch (editable) {
    case editableType === EditableType.selector && editing:
      childNode = (
        <Form.Item
          style={{
            margin: 0,
          }}
          name={dataIndex}
          rules={[
            {
              required: mandatory,
              message: `${title} ${Locale.get("profileAttachment.isRequired")}`,
            },
          ]}
        >
          <Select
            ref={inputRef}
            defaultOpen
            onBlur={save}
            showSearch
            onSearch={(e) => {
              var customIncomeType = {
                code: "other",
                label: `Other-${e}`,
              };
              var buffer = [...selectionBuffer];
              buffer[buffer.length - 1] = customIncomeType;
              setSelection(buffer);
              handleSave({ ...record, label: e }, record.key);
            }}
            onChange={() => {
              inputRef.current.blur();
            }}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {selectionBuffer.map((value) => (
              <Select.Option key={value.code} value={value.label}>
                {value.code === "other" ? `Others-${value.label}` : value.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      );
      break;

    case editableType === EditableType.number && editing:
      childNode = (
        <Form.Item
          style={{
            margin: 0,
          }}
          name={dataIndex}
          rules={[
            {
              required: true,
              message: `${title} ${Locale.get("profileAttachment.isRequired")}`,
            },
            {
              type: "number",
              message: Locale.get("profileAttachment.invalidNumberFormat"),
            },
          ]}
          //   initialValue={record[dataIndex]}
          initialValue={0}
        >
          <InputNumber
            min={0}
            precision={2}
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            placeholder="100.00"
            style={{ width: "100%" }}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value.replace(/(,*)/g, "")}
          />
        </Form.Item>
      );
      break;

    case editableType === EditableType.text && editing:
      childNode = (
        <Form.Item
          style={{
            margin: 0,
          }}
          name={dataIndex}
          rules={[
            {
              required: mandatory,
              message: `${title} ${Locale.get("profileAttachment.isRequired")}`,
            },
          ]}
          initialValue={""}
        >
          <Input
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            placeholder={title}
            style={{ width: "100%" }}
          />
        </Form.Item>
      );
      break;

    case editableType === EditableType.date && editing:
      childNode = (
        <Form.Item
          style={{
            margin: 0,
          }}
          name={dataIndex}
          rules={[
            {
              required: mandatory,
              message: `${title} ${Locale.get("profileAttachment.isRequired")}`,
            },
          ]}
          initialValue={""}
        >
          <DatePicker ref={inputRef} />
        </Form.Item>
      );
      break;

    default:
      childNode = (
        <div
          className="editable-cell-value-wrap"
          style={{
            paddingRight: 24,
          }}
          onClick={toggleEdit}
        >
          {children}
        </div>
      );
      break;
  }

  return <td {...restProps}>{childNode}</td>;
};

const EditableTable = ({
  columns = [
    {
      title: String,
      index: String,
      key: String,
      editable: Boolean,
      editableType: String,
      selection: Array,
    },
  ],
  initialValue = [],
  defaultValue,
  loading,
  value,
  size = "middle",
  onChange,
  hideAddRow = false,
  ...props
}) => {
  const [saveReminder, setSaveReminder] = useState(false);
  const [dataSource, setDataSource] = useState([]);

  useEffect(() => {
    setDataSource(
      initialValue.map((x) => {
        if (x.key) {
          return x;
        }
        return {
          ...x,
          key: randomNumberGenerator(),
        };
      })
    );
  }, [defaultValue]);

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  useEffect(() => {
    onChange(dataSource);
  }, [dataSource]);

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, { ...item, ...row });
    value = newData;
    setDataSource(newData);
    setSaveReminder(true);
  };

  const handleAdd = () => {
    const newData = {
      ...defaultValue,
      key: randomNumberGenerator(),
      //   type: "other",
    };
    value = newData;
    setDataSource([...dataSource, newData]);
    setSaveReminder(true);
  };

  const handleDelete = (dataIndex) => {
    var newData = dataSource.filter((item, index) => index !== dataIndex);
    value = newData;
    setDataSource(newData);
    setSaveReminder(true);
  };

  const column = [
    ...columns,
    {
      title: Locale.get("profileAttachment.dataColumn_e"),
      align: "center",
      width: "10%",
      render: (value, record, index) => {
        return (
          <Button
            type="link"
            style={{ color: "red" }}
            disabled={loading}
            onClick={() => handleDelete(index)}
          >
            <DeleteOutlined />
          </Button>
        );
      },
    },
  ].map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        title: col.title,
        editable: col.editable,
        dataIndex: col.dataIndex,
        selection: col.selection,
        editableType: col.editableType,
        handleSave: handleSave,
        mandatory: col.mandatory,
      }),
    };
  });

  return (
    <>
      <Table
        size={size}
        rowKey="key"
        components={components}
        rowClassName={() => "editable-row"}
        dataSource={dataSource}
        columns={column}
        pagination={false}
        loading={props.loading}
        style={{
          marginBottom: 16,
          ...props.style,
        }}
        bordered
      />

      {!hideAddRow && (
        <Button
          onClick={handleAdd}
          type="dashed"
          style={{
            marginTop: 16,
            width: "100%",
            borderColor: "#d7a301",
            color: "#d7a301",
          }}
        >
          <PlusOutlined /> {Locale.get("profileAttachment.addRow")}
        </Button>
      )}

      <Prompt
        when={saveReminder}
        message={Locale.get("profileAttachment.leaveBeforeSave")}
      />
    </>
  );
};

export default EditableTable;
